import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { pick, useContent } from "@/lib/content";

/**
 * Photo gallery, fed from the admin. Images come in three fingerprinted WebP
 * widths with a JPEG fallback, so the browser picks the smallest that fits and
 * caches it forever.
 */

const srcSet = (file) =>
  [480, 960, 1600].map((w) => `/media/${file}-${w}.webp ${w}w`).join(", ");

const Lightbox = ({ items, index, onClose, onStep, t, lang }) => {
  const item = items[index];

  // Keyboard is the natural way through a lightbox on desktop.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onStep(-1);
      if (e.key === "ArrowRight") onStep(1);
    };
    window.addEventListener("keydown", onKey);
    // Stop the page scrolling behind the overlay.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, onStep]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label={t.gallery.close}
        className="absolute right-5 top-5 font-mono text-xs uppercase tracking-[0.2em] text-bone/70 hover:text-bone"
      >
        {t.gallery.close} ✕
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onStep(-1); }}
        aria-label={t.gallery.prev}
        className="absolute left-3 z-10 px-4 py-6 text-3xl text-bone/60 hover:text-bone md:left-8"
      >
        ‹
      </button>

      <figure className="max-h-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={`/media/${item.file}-1600.webp`}
          alt={pick(item.caption, lang) || ""}
          className="max-h-[80vh] w-auto object-contain"
        />
        {pick(item.caption, lang) && (
          <figcaption className="mt-3 text-center font-body text-sm text-bone/70">
            {pick(item.caption, lang)}
          </figcaption>
        )}
      </figure>

      <button
        onClick={(e) => { e.stopPropagation(); onStep(1); }}
        aria-label={t.gallery.next}
        className="absolute right-3 z-10 px-4 py-6 text-3xl text-bone/60 hover:text-bone md:right-8"
      >
        ›
      </button>
    </div>
  );
};

export const Gallery = () => {
  const { t, lang } = useLang();
  const { data } = useContent();
  const items = data?.gallery ?? [];
  const [open, setOpen] = useState(null);

  const step = useCallback(
    (d) => setOpen((i) => (i === null ? i : (i + d + items.length) % items.length)),
    [items.length],
  );

  if (items.length === 0) return null;

  return (
    <section id="galerie" data-testid="gallery-section" className="bg-sand">
      <div className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <Reveal>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-rust">
            {t.gallery.overline}
          </span>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-display text-5xl font-medium uppercase leading-[0.9] tracking-tight text-ink md:text-7xl">
            {t.gallery.title}
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-stone">
            {t.gallery.body}
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {items.map((img, i) => (
            <Reveal key={img.id} delay={Math.min(i * 0.04, 0.3)}>
              <button
                onClick={() => setOpen(i)}
                data-testid={`gallery-item-${img.id}`}
                className="group block w-full overflow-hidden bg-hairline"
              >
                <img
                  src={`/media/${img.file}-480.webp`}
                  srcSet={srcSet(img.file)}
                  sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  alt={pick(img.caption, lang) || ""}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {open !== null && (
        <Lightbox
          items={items}
          index={open}
          onClose={() => setOpen(null)}
          onStep={step}
          t={t}
          lang={lang}
        />
      )}
    </section>
  );
};
